import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const PageContainer = styled('div', {
  base: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background:
      'linear-gradient(135deg, {colors.quickpass.surfaceCool} 0%, {colors.quickpass.surface} 60%, #FEF0FF 100%)',
    padding: '24px',
  },
})

const ContentCard = styled('div', {
  base: {
    width: '100%',
    maxWidth: '460px',
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    boxShadow: '0 24px 60px -20px rgba(128, 84, 255, 0.18)',
    padding: '32px 30px 28px 30px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
})

const Wordmark = styled('h1', {
  base: {
    fontFamily: 'display',
    fontSize: '22px',
    fontWeight: '800',
    letterSpacing: '-0.5px',
    color: 'quickpass.primary',
    margin: '0',
    textAlign: 'center',
  },
})

const Heading = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '26px',
    fontWeight: '700',
    color: 'quickpass.ink',
    margin: '2px 0 0 0',
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
})

const Subheading = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.inkSoft',
    margin: '0',
    textAlign: 'center',
    lineHeight: '1.55',
  },
})

const ListingCard = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'quickpass.surfaceCool',
    borderRadius: 'sm',
    padding: '12px',
  },
})

const ListingCardImage = styled('img', {
  base: {
    width: '64px',
    height: '64px',
    flexShrink: '0',
    borderRadius: '10px',
    objectFit: 'cover',
    backgroundColor: '#E5E0F5',
  },
})

const ListingCardBody = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    minWidth: '0',
  },
})

const ListingCardTitle = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})

const ListingCardSubtitle = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})

const ListingCardMetaRow = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '4px 10px',
    marginTop: '1px',
  },
})

const ListingCardPrice = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '700',
    color: 'quickpass.primary',
  },
})

const ListingCardMeta = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
  },
})

const Form = styled('form', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '4px',
  },
})

const FieldRow = styled('div', {
  base: {
    display: 'flex',
    gap: '12px',
    '& > *': {
      flex: '1 1 0',
      minWidth: '0',
    },
  },
})

const FieldGroup = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
})

const Label = styled('label', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    letterSpacing: '0.2px',
  },
})

const TextInput = styled('input', {
  base: {
    width: '100%',
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    padding: '11px 13px',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      boxShadow: '0 0 0 3px rgba(128, 84, 255, 0.18)',
    },
    _placeholder: {
      color: 'quickpass.inkSoft',
    },
  },
})

const SelectInput = styled('select', {
  base: {
    width: '100%',
    fontFamily: 'body',
    fontSize: '15px',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    padding: '11px 13px',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      boxShadow: '0 0 0 3px rgba(128, 84, 255, 0.18)',
    },
  },
})

const MessageTextarea = styled('textarea', {
  base: {
    width: '100%',
    minHeight: '110px',
    resize: 'vertical',
    fontFamily: 'body',
    fontSize: '15px',
    lineHeight: '1.5',
    color: 'quickpass.ink',
    backgroundColor: '#FFFFFF',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    borderRadius: 'sm',
    padding: '12px 13px',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}',
    _focus: {
      outline: 'none',
      borderColor: 'quickpass.primary',
      boxShadow: '0 0 0 3px rgba(128, 84, 255, 0.18)',
    },
    _placeholder: {
      color: 'quickpass.inkSoft',
    },
  },
})

const SendingAs = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    textAlign: 'center',
    '& strong': {
      color: 'quickpass.ink',
      fontWeight: '700',
    },
  },
})

const PrimaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.primary',
    border: 'none',
    borderRadius: 'pill',
    paddingX: '22px',
    paddingY: '13px',
    cursor: 'pointer',
    marginTop: '4px',
    transition:
      'transform {durations.fast} {easings.appleStandard}, box-shadow {durations.fast} {easings.appleStandard}, opacity {durations.fast} {easings.appleStandard}',
    boxShadow: '0 8px 22px -8px rgba(128, 84, 255, 0.55)',
    _hover: {
      transform: 'translateY(-1px)',
      boxShadow: '0 12px 28px -10px rgba(128, 84, 255, 0.65)',
    },
    _disabled: {
      opacity: '0.55',
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
  },
})

const SecondaryButton = styled('button', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '600',
    color: 'quickpass.inkSoft',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    padding: '4px',
    alignSelf: 'center',
    _hover: {
      color: 'quickpass.primary',
    },
  },
})

const PrivacyNote = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
    textAlign: 'center',
    lineHeight: '1.5',
    margin: '2px 0 0 0',
  },
})

const ErrorBanner = styled('div', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: '#FFFFFF',
    backgroundColor: 'quickpass.warmth',
    padding: '10px 14px',
    borderRadius: 'sm',
    marginTop: '4px',
  },
})

const StatusIndicator = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    marginTop: '8px',
  },
})

const Spinner = styled(motion.div, {
  base: {
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    border: '2.5px solid',
    borderColor: '#E5E0F5',
    borderTopColor: 'quickpass.primary',
  },
})

const SuccessIcon = styled('div', {
  base: {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: 'quickpass.primary',
    color: '#FFFFFF',
    fontSize: '32px',
    fontWeight: '800',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    boxShadow: '0 10px 28px -10px rgba(128, 84, 255, 0.65)',
  },
})

export {
  PageContainer,
  ContentCard,
  Wordmark,
  Heading,
  Subheading,
  ListingCard,
  ListingCardImage,
  ListingCardBody,
  ListingCardTitle,
  ListingCardSubtitle,
  ListingCardMetaRow,
  ListingCardPrice,
  ListingCardMeta,
  Form,
  FieldRow,
  FieldGroup,
  Label,
  TextInput,
  SelectInput,
  MessageTextarea,
  SendingAs,
  PrimaryButton,
  SecondaryButton,
  PrivacyNote,
  ErrorBanner,
  StatusIndicator,
  Spinner,
  SuccessIcon,
}
